/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Toolbar,
    TOOLBAR_ITEM_TYPE,
    toolbarItemDecorators,
    useGridApi,
    useModal,
} from '@veeam-vspc/components';

import type { ToolbarItems } from '@veeam-vspc/components';

import { useLang } from 'views/providers/LangProvider/hooks';
import { ResourceDialog } from '../ResourceDialog';
import addIcon from 'images/actions/add.svg';
import removeIcon from 'images/actions/remove.svg';
import editIcon from 'images/actions/edit.svg';

import type { ReplicaResource } from 'core/interfaces';

export const ActionsToolbar = ({ hwPlans, cloudConnectAgentUid }: { hwPlans: ReplicaResource[]; cloudConnectAgentUid: string; }) => {
    const lang = useLang();
    const gridApi = useGridApi<ReplicaResource, void, void>();

    const [quotaDialog, quotaDialogActions] = useModal<ReplicaResource>({
        render: ({ deactivate, data }) => (
            <ResourceDialog
                data={data}
                existingItems={hwPlans}
                cloudConnectAgentUid={cloudConnectAgentUid}
                onConfirm={(newData) => {
                    if (data) {
                        hwPlans[hwPlans.findIndex(item => item.cloudConnectResourceUid === data.cloudConnectResourceUid)] = newData;
                    } else {
                        hwPlans.push(newData);
                    }
                    gridApi.reloadGrid();
                }}
                deactivate={deactivate}
            />
        ),
    });

    const toolbarItems: ToolbarItems = [
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: addIcon,
            text: lang.ADD,
            onClick: () => {
                quotaDialogActions.activate();
            },
            decorators: [
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: editIcon,
            text: lang.EDIT,
            onClick: async() => {
                const selected = gridApi.selected[0];
                quotaDialogActions.activate(selected);
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
        {
            type: TOOLBAR_ITEM_TYPE.button,
            iconSrc: removeIcon,
            text: lang.REMOVE,
            onClick: async() => {
                const selected = gridApi.selected[0];
                hwPlans.splice(hwPlans.findIndex(item => item.cloudConnectResourceUid === selected.cloudConnectResourceUid), 1);
                gridApi.reloadGrid();
            },
            decorators: [
                toolbarItemDecorators.disallowWithoutSingleSelection(),
            ],
        },
    ];

    return (
        <>
            <Toolbar items={toolbarItems} />
            {quotaDialog}
        </>
    );
};
